/**
 * @file date-helpers.ts
 * @description Shared date-bucketing helpers used by countdown chips
 *   (slice 59 quote expiry, slice 69 project deadline, future ones).
 *   The two non-obvious things this module pins:
 *
 *   1. `YYYY-MM-DD` is parsed as LOCAL midnight, not UTC midnight.
 *      Browsers' Date.parse('2026-05-23') returns 2026-05-23T00:00:00Z;
 *      compared against today's local midnight that's off by the TZ
 *      offset, producing off-by-one days in any TZ that isn't UTC.
 *      The slice-70 fix routes all date-only strings through
 *      parseLocalDate so chips read consistently in the user's calendar.
 *
 *   2. The day diff uses Math.round, not Math.ceil. With local-midnight
 *      both sides the diff is an exact integer multiple of 86_400_000
 *      ms except across DST boundaries (±1 hour); round absorbs the
 *      DST shift correctly, ceil would push 1.04 days up to 2.
 *
 * @module Lib
 * @security Pure functions, no PII, no network. Easy to test, safe to
 *   inline in list rows without performance concern.
 */

const MS_PER_DAY = 86_400_000;

/** Parse a date-only string (`YYYY-MM-DD`) as the user's local midnight.
 *  Falls back to Date.parse for any other shape so callers that pass a
 *  full ISO timestamp still work. Returns null on parse failure. */
export function parseLocalDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m !== null) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const ms = Date.parse(s);
  return Number.isNaN(ms) ? null : new Date(ms);
}

/** Whole days from local midnight today to the target date. Negative
 *  for past dates, 0 for today, positive for future. Returns null on
 *  parse failure. `now` is injected for tests. */
export function daysUntil(isoDate: string, now: Date = new Date()): number | null {
  const target = parseLocalDate(isoDate);
  if (target === null) return null;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / MS_PER_DAY);
}
